/**
 * OpenWoundSkillEffect — Blades tree rend-path apotheosis.
 *
 * Detonates all active bleed stacks on the target simultaneously —
 * 25 damage per stack consumed, 75 total at maximum (3 stacks).
 * Crit-eligible, Whetstone-amplified via the 'open_wound' attack type
 * (blade-scoped, so the combat resolver applies crit and Whetstone).
 * The bleed DoT stops ticking as all stacks are consumed. Very long
 * cooldown (4000ms).
 *
 * Source-agnostic: detonates ALL bleed stacks regardless of origin
 * (Rend, Sever, Specter, etc.). Cross-tree builds with multiple
 * bleed sources get a larger detonation.
 *
 * Exsanguinate does NOT apply — the detonation is a single burst hit
 * (attack type 'open_wound'), not a bleed tick ('bleed_tick'). The
 * resolver distinguishes these by attack type.
 *
 * Slot cost: 1 (per design-decisions.md — all apotheosis nodes cost 1).
 *
 * Implements SkillEffect directly with own cooldown management
 * (InstantSkillEffect has no cooldown). Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see blades-spec.md § Open Wound
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface OpenWoundDeps {
  readonly statusEffects: {
    getStacks(effectId: string): number;
    remove(effectId: string): void;
  };
  readonly combat: {
    applyDetonationDamage(attackType: string, damage: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface OpenWoundConfig {
  readonly cooldownMs: number;
  readonly damagePerStack: number;
}

const DEFAULT_CONFIG: OpenWoundConfig = {
  cooldownMs: 4000,         // very_long cooldown tier
  damagePerStack: 25,       // flat 25 damage per bleed stack consumed
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class OpenWoundSkillEffect implements SkillEffect {
  readonly skillId = 'open_wound';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: OpenWoundDeps;
  private readonly config: OpenWoundConfig;

  private _cooldownRemaining = 0;
  private _detonationCount = 0;
  private _stacksConsumed = 0;
  private _lastDamageDealt = 0;

  constructor(deps: OpenWoundDeps, config: Partial<OpenWoundConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get detonationCount(): number { return this._detonationCount; }
  get stacksConsumed(): number { return this._stacksConsumed; }
  get lastDamageDealt(): number { return this._lastDamageDealt; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Read bleed stacks (source-agnostic — all sources included)
    const stacks = this.deps.statusEffects.getStacks('bleed');
    this._stacksConsumed = stacks;

    // Calculate detonation damage: flat per-stack × count
    this._lastDamageDealt = stacks * this.config.damagePerStack;

    // Consume all bleed stacks (removes the DoT entirely)
    if (stacks > 0) {
      this.deps.statusEffects.remove('bleed');
    }

    // Fire the detonation hit (crit/Whetstone resolved by combat system)
    this.deps.combat.applyDetonationDamage('open_wound', this._lastDamageDealt);
    this._detonationCount++;

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
